import {
  DestroyRef,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { Params, UrlSegment } from '@angular/router';
import { Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { BoelsAlternateLinkService } from './links/boels-alternate-link.service';
import {
  BoelsBaseSite,
  forbiddenRobotsQueryParams,
  Page,
  PageRobotsMeta,
  PageTemplate,
  PageType,
} from '@boels/models';
import { SiteContextFacade, StoreFinderFacade } from '@boels/shared-state';
import { CanonicalLinkService } from './links/canonical-link.service';
import {
  FALLBACK_TITLE,
  LinksService,
  MetaDataService,
  parseUrl,
  RoutingService,
  WindowRef,
} from '@boels/helpers';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class BoelsSeoMetaService {
  #rendererFactory = inject(RendererFactory2);
  #boelsAlternateLinkService = inject(BoelsAlternateLinkService);
  #canonicalLinkService = inject(CanonicalLinkService);
  #siteContextFacade = inject(SiteContextFacade);
  #storeFinderFacade = inject(StoreFinderFacade);
  #boelsLinksService = inject(LinksService);
  #winRef = inject(WindowRef);
  #translocoService = inject(TranslocoService);
  #metaDataService = inject(MetaDataService);
  #routingService = inject(RoutingService);
  #destroyRef = inject(DestroyRef);

  readonly #currentPage$: Observable<[Page, BoelsBaseSite]> =
    this.#routingService
      .getRoutePropertyWithValueAsObservable<
        Page,
        'data'
      >('data', 'routeResolver')
      .pipe(
        filter(Boolean),
        withLatestFrom(this.#siteContextFacade.getActiveBaseSiteEntity()),
        tap(([page, baseSite]) => this.handleOnPageChange(baseSite, page)),
        takeUntilDestroyed(this.#destroyRef),
      );

  readonly #renderer: Renderer2 = this.#rendererFactory.createRenderer(
    null,
    null,
  );

  init(): void {
    this.#currentPage$.subscribe();
  }

  handleOnPageChange(baseSite: BoelsBaseSite, page?: Page): void {
    // Handle the Meta Data for the Page
    if (page && page.typeCode === PageType.CONTENT_PAGE) {
      if (page.title) {
        this.#metaDataService.setTitle(page.title ?? FALLBACK_TITLE);
      }
      this.#metaDataService.setMetaDescription(page.description);
    }

    this.#boelsAlternateLinkService.resetRegionalHrefLangs(); // remove Alternates before adding new ones
    switch (page?.template) {
      case PageTemplate.PRODUCTLIST:
      case PageTemplate.CATEGORY:
        this.#boelsAlternateLinkService.appendProductListHrefLangs(baseSite);
        this.setCanonical(baseSite);
        break;
      case PageTemplate.PRODUCT:
        this.#boelsAlternateLinkService.appendProductDetailHrefLangs(baseSite);
        this.setCanonical(baseSite);
        break;
      case PageTemplate.STOREFINDER: {
        this.#routingService
          .getRoutePropertyAsObservable('params')
          .pipe(
            tap((routeParams) => {
              this.setCanonical(baseSite);

              if (
                routeParams &&
                !Object.keys(routeParams).some(
                  (key) => key === 'region' || key === 'store',
                )
              ) {
                this.setHreflangs(baseSite);
              }
            }),
            take(1),
          )
          .subscribe();

        // Override the Meta Title when a Store detail page is active
        this.#storeFinderFacade
          .getActiveStore()
          .pipe(
            filter((store) => !!store),
            switchMap((store) => {
              const metaTitle$ = store?.metaTitle
                ? of(store.metaTitle)
                : this.#translocoService.selectTranslate(
                    'storeDetails.metaTitle',
                    { displayName: store?.displayName },
                    'storeFinder',
                  );

              const metaDescription$ = store?.metaDescription
                ? of(store.metaDescription)
                : this.#translocoService.selectTranslate(
                    'storeDetails.metaDescription',
                    { displayName: store?.displayName },
                    'storeFinder',
                  );

              return metaTitle$.pipe(
                tap((title) => this.#metaDataService.setTitle(title)),
                switchMap(() => metaDescription$),
              );
            }),
            take(1),
          )
          .subscribe((description) =>
            this.#metaDataService.setMetaDescription(description),
          );

        // Override the Meta Title when a region page is active
        this.#storeFinderFacade
          .getActiveRegion()
          .pipe(
            filter((region) => !!region),
            switchMap((region) =>
              this.#translocoService.selectTranslate(
                'result.regionMetaTitle',
                {
                  region: region
                    ?.split('-')
                    .map((word) =>
                      word
                        .split(' ')
                        .map(
                          (subWord) =>
                            subWord.charAt(0).toUpperCase() + subWord.slice(1),
                        )
                        .join(' '),
                    )
                    .join('-'),
                },
                'storeFinder',
              ),
            ),
            take(1),
          )
          .subscribe((region) => {
            if (region) {
              this.#metaDataService.setTitle(region);
            }
          });

        break;
      }
      case PageTemplate.ERRORPAGE:
        break;
      default:
        if (
          page?.typeCode === PageType.CONTENT_PAGE &&
          page?.showHrefLangTags === 'true'
        ) {
          this.setHreflangs(baseSite);
        }
        if (this.isIndexRobot(page)) {
          this.setCanonical(baseSite);
        } else {
          this.#canonicalLinkService.removeCanonical();
        }
    }
    const robotTagValue: string = page?.robotTag?.toString() ?? '';
    if (robotTagValue) {
      this.setRobotMetaTag(robotTagValue);
    }
  }

  setRobotMetaTag(robotMetaTagValue: string) {
    robotMetaTagValue = robotMetaTagValue?.replace('_', ', ');
    const existingRobotMetaTag: Element | null =
      this.#winRef.document.head.querySelector('meta[name=robots]');

    const routeQueryParams = this.#routingService.getRouteProperty(
      'queryParams',
    ) as Params;
    const robotTagContent = Object.keys(routeQueryParams).some((item) =>
      forbiddenRobotsQueryParams.includes(item),
    )
      ? 'NOINDEX, NOFOLLOW'
      : robotMetaTagValue;

    if (existingRobotMetaTag) {
      // update robot tag content if already exists
      existingRobotMetaTag.textContent = robotTagContent;
    } else {
      // create actual robot tag if not exist
      const robotTag: HTMLMetaElement = this.#renderer.createElement('meta');
      this.#renderer.setAttribute(robotTag, 'name', 'robots');
      this.#renderer.setAttribute(robotTag, 'content', robotTagContent);
      this.#renderer.appendChild(this.#winRef.document.head, robotTag);
    }

    if (robotTagContent.toUpperCase().includes('NOINDEX')) {
      this.#canonicalLinkService.removeCanonical();
    }
  }

  setCanonical(baseSite: BoelsBaseSite): void {
    const baseSiteUrl = baseSite?.baseSiteUrl as string;
    const urlSegments = this.#routingService.getRouteProperty(
      'url',
    ) as UrlSegment[];
    const parsedUrl = parseUrl(urlSegments.join('/'));

    if (parsedUrl.culture) {
      this.#canonicalLinkService.setCanonical(
        this.#boelsLinksService.urlBuilder(
          baseSiteUrl,
          parsedUrl.culture,
          parsedUrl.path ?? '',
        ),
      );
    }
  }

  setHreflangs(baseSite: BoelsBaseSite): void {
    const baseSiteUrl = baseSite?.baseSiteUrl as string;
    this.#boelsAlternateLinkService.getAlternatesForContentPages(baseSiteUrl);
  }

  private isIndexRobot(page?: Page) {
    return !!page?.robotTag
      ?.split('_')
      .find((tag) => tag === PageRobotsMeta.INDEX);
  }
}
